import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import portalService from '../../services/portalService';
import { formatCurrency } from '../../utils/amountUtils';
import { formatDate } from '../../utils/dateUtils';
import StatusBadge from '../shared/StatusBadge';

function PortalInvoiceList() {
  const context = useOutletContext();
  const publicId = context?.customerPublicId || context?.customerInfo?.customer?.publicId || context?.portalData?.customerPublicId;
  const navigate = useNavigate();
  
  console.log('PortalInvoiceList - context:', context);
  console.log('PortalInvoiceList - publicId:', publicId);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const fetchInvoices = useCallback(async () => {
    if (!publicId) return;
    
    try {
      setLoading(true);
      const response = await portalService.getInvoices(publicId, {
        status: statusFilter,
        page: pagination.page,
        limit: pagination.limit
      });
      
      setInvoices(response.invoices);
      setPagination(response.pagination);
      
      // Clear selections when filter changes
      setSelectedInvoices([]);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [publicId, statusFilter, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // formatCurrency utility now imported from amountUtils
  // formatDate utility now imported from dateUtils (timezone-safe; standardizes
  // on long-form date — was previously short-form via no-arg toLocaleDateString)

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      }
      return [...prev, invoiceId];
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.filter(inv => inv.canPay).length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.filter(inv => inv.canPay).map(inv => inv.id));
    }
  };

  const handlePaySelected = () => {
    console.log('🎯 handlePaySelected called with:', {
      selectedInvoices,
      selectedCount: selectedInvoices.length,
      publicId,
      context
    });
    
    navigate(`../pay`, {
      state: { 
        selectedInvoices,
        publicId,
        customerInfo: context?.customerInfo,
        portalData: context?.portalData,
        fromUserAction: true  // Flag to prevent auto-redirect interference
      }
    });
  };

  const getTotalSelected = () => {
    return invoices
      .filter(inv => selectedInvoices.includes(inv.id))
      .reduce((sum, inv) => {
        // Calculate amount due - backend sends dollar amounts
        const amountDue = (inv.totalAmount || 0) - (inv.amountPaid || 0);
        return sum + amountDue;
      }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Pay Invoices</h2>
        <p className="text-gray-600 mt-1">Select invoices to pay</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filter by status:</label>
          <div className="flex gap-2">
            {['open', 'overdue', 'all'].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setStatusFilter(status);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  statusFilter === status
                    ? 'bg-brand text-white font-medium'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Selection Summary */}
      {selectedInvoices.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">
                {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? 's' : ''} selected
              </p>
              <p className="text-lg font-bold text-blue-900 mt-1">
                Total: {formatCurrency(getTotalSelected())}
              </p>
            </div>
            <button
              onClick={handlePaySelected}
              className="px-4 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors"
            >
              Pay Selected
            </button>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="mt-2 text-gray-600">No invoices found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.length === invoices.filter(inv => inv.canPay).length && invoices.filter(inv => inv.canPay).length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount Due
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        {invoice.canPay && (
                          <input
                            type="checkbox"
                            checked={selectedInvoices.includes(invoice.id)}
                            onChange={() => handleSelectInvoice(invoice.id)}
                            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(invoice.invoiceDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(invoice.dueDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge 
                          type="invoice_status" 
                          value={{
                            status: invoice.status,
                            paymentStatus: invoice.paymentStatus,
                            dueDate: invoice.dueDate,
                            displayStatus: invoice.displayStatus
                          }}
                          size="xs"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {formatCurrency((invoice.totalAmount || 0) - (invoice.amountPaid || 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => navigate(`${invoice.invoiceNumber}`)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className={`px-3 py-1 text-sm rounded-md ${
                      pagination.page === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className={`px-3 py-1 text-sm rounded-md ${
                      pagination.page === pagination.pages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PortalInvoiceList;